// Admin StudentDetailPage — student info card, enrollments table, payments table (with inline
// "Mark paid", same logic as the dashboard). Rendered by UserDetailPage, which has already loaded the user.
import { useFetch } from "../../hooks/useApi.js";
import Badge from "../../components/shared/Badge.js";
import StudentPaymentRow from "../../components/admin/StudentPaymentRow.js";
import DetailHeader from "../../components/admin/DetailHeader.js";
import DetailSection from "../../components/admin/DetailSection.js";
import InfoField from "../../components/admin/InfoField.js";
import PaymentStatusBadge from "../../components/shared/PaymentStatusBadge.js";
import { formatDate } from "../../lib/formatDateTime.js";
import { KENYA_COUNTRY_CODE, MAX_PAGE_SIZE } from "@dm-lms/shared/constants";
import { getCountryName } from "@dm-lms/shared/utils";
import { PAYMENT_OPTIONS, PROFESSION_OPTIONS, REGION_LABEL } from "../../lib/profileFormOptions.js";
import type { StudentDetail, StudentEnrollment } from "../../lib/adminStudentTypes.js";
import type { PaymentWithProof, PaginatedResult, Profession } from "@dm-lms/shared/types";

function professionLabel(value: Profession | null): string {
  if (!value) return "—";
  return PROFESSION_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

function paymentMethodLabel(value: string | null): string {
  if (!value) return "—";
  return PAYMENT_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

export default function StudentDetailPage({ student }: { student: StudentDetail }) {
  const { data: enrollmentsData, isLoading: enrollmentsLoading, error: enrollmentsError } = useFetch<PaginatedResult<StudentEnrollment>>(
    `/api/enrollments/student/${student.id}?page=1&pageSize=50`
  );
  const { data: paymentsData, isLoading: paymentsLoading, error: paymentsError } = useFetch<PaginatedResult<PaymentWithProof>>(
    `/api/payments?studentId=${student.id}&page=1&pageSize=${MAX_PAGE_SIZE}`
  );
  const enrollments = enrollmentsData?.items ?? [];
  const payments = paymentsData?.items ?? [];
  const profile = student.profile;

  return (
    <div className="max-w-3xl space-y-6">
      {/* SECTION 1 — Student info */}
      <DetailSection
        header={
          <DetailHeader
            user={student}
            badge={
              <Badge tone={profile?.profileComplete ? "green" : "gold"}>
                {profile?.profileComplete ? "Profile complete" : "Profile incomplete"}
              </Badge>
            }
          />
        }
      >
        <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
          <InfoField label="Student code">{student.code}</InfoField>
          <InfoField label="Member since">{formatDate(student.createdAt)}</InfoField>
          <InfoField label="Phone">{profile?.phone ?? "—"}</InfoField>
          <InfoField label="Organization">{profile?.organization ?? "—"}</InfoField>
          <InfoField label="Profession">{professionLabel(profile?.profession ?? null)}</InfoField>
          <InfoField label="Country">{getCountryName(profile?.country ?? null)}</InfoField>
          <InfoField label={profile?.country === KENYA_COUNTRY_CODE ? "County" : REGION_LABEL}>
            {(profile?.country === KENYA_COUNTRY_CODE ? profile?.county : profile?.region) ?? "—"}
          </InfoField>
          <InfoField label="Preferred payment method">{paymentMethodLabel(profile?.preferredPaymentMethod ?? null)}</InfoField>
        </div>
      </DetailSection>

      {/* SECTION 2 — Enrollments */}
      <DetailSection
        title="Enrollments"
        isLoading={enrollmentsLoading}
        error={enrollmentsError}
        isEmpty={enrollments.length === 0}
        emptyText="No enrollments yet."
      >
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-dm-slate">
              <tr>
                <th className="px-4 py-3 font-medium">Course</th>
                <th className="px-4 py-3 font-medium">Payment status</th>
                <th className="px-4 py-3 font-medium">Enrolled</th>
                <th className="px-4 py-3 font-medium">Completed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {enrollments.map((enrollment) => (
                <tr key={enrollment.id}>
                  <td className="px-4 py-3 font-medium text-dm-navy">{enrollment.courseTitle}</td>
                  <td className="px-4 py-3"><PaymentStatusBadge status={enrollment.paymentStatus} /></td>
                  <td className="px-4 py-3">{formatDate(enrollment.enrolledAt)}</td>
                  <td className="px-4 py-3">{enrollment.completedAt ? formatDate(enrollment.completedAt) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DetailSection>

      {/* SECTION 3 — Payments */}
      <DetailSection
        title="Payments"
        isLoading={paymentsLoading}
        error={paymentsError}
        isEmpty={payments.length === 0}
        emptyText="No payments yet."
      >
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-dm-slate">
              <tr>
                <th className="px-4 py-3 font-medium">Code</th>
                <th className="px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Method</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Paid date</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {payments.map((payment) => (
                <StudentPaymentRow key={payment.id} payment={payment} />
              ))}
            </tbody>
          </table>
        </div>
        {paymentsData?.hasMore && (
          <p className="mt-2 text-xs text-dm-slate">Showing the latest {MAX_PAGE_SIZE} payments.</p>
        )}
      </DetailSection>
    </div>
  );
}
